import {
  createEvent,
  combine,
  Store,
  sample,
  createEffect,
  restore,
  StoreWritable
} from 'effector'
import {
  VacancyResource,
  UserResource,
  InterviewResource,
  MessageResource,
  MessageResourceAttachmentsItem,
  ImageAttachment,
  VacancyMeetingMode
} from 'api/endpoints/openapi-generated.schemas'
import { Message } from '../call'
import { $user } from 'providers/auth-provider'
import { getVacancy, getVacancyDataFromEvent } from 'hooks/use-vacancy'
import { InterviewStageEnum } from 'consts/company-alias.consts'
import { getInterview, getInterviewDataFromEvent } from 'hooks/use-interview'
import { getInterviewStateDataFromEvent } from 'hooks/use-interview-status'
import { MessageTypeEnum } from 'constants/call'
import { QueryCacheNotifyEvent } from '@tanstack/react-query'
import { queryCacheService } from '../query-cache-service'
import { MeetingModeFe } from './interview-ui.types'

export type State = {
  vacancy: VacancyResource
  interview: InterviewResource
  user: UserResource
  initialMessages: Array<Message>
}

type VacancyLoadedPayload = VacancyResource | null

type InterviewLoadedPayload = InterviewResource | null

type InitialMessagesPayload = Array<Message> | null

class CallFixturesService {
  readonly $user = $user

  readonly setVacancyId = createEvent<string | null>()
  readonly $vacancyId = restore(this.setVacancyId, null)

  private readonly vacancyLoaded = createEvent<VacancyLoadedPayload>()
  readonly $vacancy = restore(this.vacancyLoaded, null)

  readonly setStage = createEvent<InterviewStageEnum | null>()
  readonly $stage = restore(this.setStage, null)

  readonly setInterviewId = createEvent<string>()
  readonly $interviewId = restore(this.setInterviewId, null)

  private readonly interviewLoaded = createEvent<InterviewLoadedPayload>()
  readonly $interview = restore(this.interviewLoaded, null)

  readonly setInitialMeetingMode = createEvent<MeetingModeFe>()
  readonly setInitialMeetingModeOnInterviewLoad = createEvent<MeetingModeFe>()
  readonly $initialMeetingMode = restore(this.setInitialMeetingMode, null)

  private readonly initialMessagesLoaded = createEvent<InitialMessagesPayload>()
  readonly $initialMessages = restore(this.initialMessagesLoaded, null)

  readonly $state: Store<State | null>
  private readonly queryCacheEvent = createEvent<QueryCacheNotifyEvent>()

  constructor() {
    this.loadVacancyStateLogic()
    this.loadInterviewStateLogic()
    this.loadInitialMessagesLogic()

    this.$state = combine(
      {
        vacancy: this.$vacancy,
        interview: this.$interview,
        user: this.$user,
        initialMessages: this.$initialMessages
      },
      (s) => {
        const { vacancy, interview, user, initialMessages } = s

        if (!!vacancy && !!interview && !!user && !!initialMessages) {
          return { vacancy, interview, user, initialMessages }
        }
        return null
      }
    )

    sample({
      clock: this.setInitialMeetingModeOnInterviewLoad,
      source: this.$initialMeetingMode,
      filter: (oldMeetingMode) => !oldMeetingMode,
      fn: (_, newMeetingMode) => newMeetingMode,
      target: this.setInitialMeetingMode
    })
  }

  private loadVacancyStateLogic = (): void => {
    sample({
      clock: this.queryCacheEvent,
      source: this.$vacancyId,
      fn: (vacancyId, queryCacheEvent) => {
        return { vacancyId, queryCacheEvent }
      },
      target: createEffect(this.handleVacancyQueryCacheEvent)
    })

    const loadVacancyFromCacheFx = createEffect(
      (id: string): VacancyLoadedPayload => {
        return getVacancy(id) ?? null
      }
    )

    sample({
      clock: loadVacancyFromCacheFx.doneData,
      filter: Boolean,
      target: this.vacancyLoaded
    })

    sample({
      clock: this.$vacancyId,
      filter: Boolean,
      target: loadVacancyFromCacheFx
    })
  }

  private loadInterviewStateLogic = (): void => {
    sample({
      clock: this.queryCacheEvent,
      source: this.$interviewId,
      fn: (interviewId, queryCacheEvent) => {
        return { interviewId, queryCacheEvent }
      },
      target: createEffect(this.handleInterviewQueryCacheEvent)
    })

    const loadInterviewFromCacheFx = createEffect(
      (id: string): InterviewLoadedPayload => {
        return getInterview(id) ?? null
      }
    )

    sample({
      clock: loadInterviewFromCacheFx.doneData,
      filter: Boolean,
      target: this.interviewLoaded
    })

    sample({
      clock: this.$interviewId,
      filter: Boolean,
      target: loadInterviewFromCacheFx
    })
  }

  private loadInitialMessagesLogic = (): void => {
    sample({
      clock: this.queryCacheEvent,
      source: {
        vacancy: this.$vacancy,
        interview: this.$interview
      },
      fn: ({ vacancy, interview }, queryCacheEvent) => {
        return { vacancy, interview, queryCacheEvent }
      },
      target: createEffect(this.handleInterviewStateQueryCacheEvent)
    })

    // const loadInitialMessagesFx = createEffect(
    //   ({
    //     vacancy,
    //     interview
    //   }: {
    //     vacancy: VacancyResource
    //     interview: InterviewResource
    //   }): InitialMessagesPayload | null => {
    //     const data = getInitialMessages(vacancy, interview)
    //     if (!data) return null
    //
    //     return this.mapMessages(data)
    //   }
    // )
    //
    // sample({
    //   clock: loadInitialMessagesFx.doneData,
    //   filter: Boolean,
    //   target: this.initialMessagesLoaded
    // })
    //
    // sample({
    //   clock: combine(
    //     {
    //       vacancy: this.$vacancy,
    //       interview: this.$interview
    //     },
    //     ({ vacancy, interview }) => {
    //       if (!vacancy || !interview) return null
    //       return { vacancy, interview }
    //     }
    //   ),
    //   filter: Boolean,
    //   target: loadInitialMessagesFx
    // })
  }

  private handleVacancyQueryCacheEvent = ({
    queryCacheEvent,
    vacancyId
  }: {
    queryCacheEvent: QueryCacheNotifyEvent
    vacancyId: string | null
  }): void => {
    if (!vacancyId) return

    const data = getVacancyDataFromEvent(queryCacheEvent, vacancyId)
    if (data === void 0) return

    this.vacancyLoaded(data?.data ?? null)
  }

  private handleInterviewQueryCacheEvent = ({
    queryCacheEvent,
    interviewId
  }: {
    queryCacheEvent: QueryCacheNotifyEvent
    interviewId: string | null
  }): void => {
    if (!interviewId) return

    const data = getInterviewDataFromEvent(queryCacheEvent, interviewId)
    if (data === void 0) return

    this.interviewLoaded(data?.data ?? null)

    this.setInitialMeetingModeOnInterviewLoad(data?.data.meeting_mode ?? null)
  }

  private handleInterviewStateQueryCacheEvent = ({
    queryCacheEvent,
    interview,
    vacancy
  }: {
    queryCacheEvent: QueryCacheNotifyEvent
    interview: InterviewResource | null
    vacancy: VacancyResource | null
  }): void => {
    if (!interview || !vacancy) return

    const data = getInterviewStateDataFromEvent(
      queryCacheEvent,
      vacancy,
      interview
    )
    if (data === void 0) return

    const mapped = !data ? null : this.mapMessages(data.data.messages)

    this.initialMessagesLoaded(mapped)

    // presentation_slides_urls отдаётся для всех слайдов вакансии независимо от
    // режима встречи (aivy-api: interview/router.py::get_interview_state →
    // file/service.py::get_vacancy_slides), поэтому непустой список сам по себе
    // ещё не значит, что кандидату нужна страница презентации. Режим встречи
    // интервью тут первичен: в chat_only презентации нет, слайды показываются
    // в пузырях переписки.
    if (
      data?.data.presentation_slides_urls?.length &&
      interview.meeting_mode !== VacancyMeetingMode.chat_only
    ) {
      this.setInitialMeetingMode('presentation_video_to_voice_transition')
    }
  }

  private mapMessages = (source: Array<MessageResource>): Array<Message> => {
    return (source || []).map((message) => ({
      type:
        message.role === 'user'
          ? MessageTypeEnum.outgoing
          : MessageTypeEnum.incoming,
      createdAt: message.created_at,
      text: message.message,
      sender: message.role,
      imageUrls: this.mapImageAttachments(message.attachments)
    }))
  }

  // слайд сохраняется вместе с репликой (aivy-api: history/service.py, колонка
  // messages.attachments), поэтому после перезагрузки страницы он берётся из
  // истории — иначе кандидат снова видит реплику про слайд без слайда
  private mapImageAttachments = (
    attachments: Array<MessageResourceAttachmentsItem> | undefined
  ): Array<string> | undefined => {
    const urls = (attachments || [])
      .filter((x): x is ImageAttachment => x.type === 'image')
      .map(({ url }) => url)
      .filter(Boolean)

    return urls.length ? urls : undefined
  }

  subscribe = (): VoidFunction => {
    const unsubscribeEvent = queryCacheService.queryCacheEvent.watch(
      (event) => {
        this.queryCacheEvent(event)
      }
    )

    if (window.isDevMode) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stores: Array<[StoreWritable<any>, string]> = [
        [this.$vacancyId, '$vacancyId'],
        [this.$vacancy, '$vacancy'],
        [this.$stage, '$stage'],
        [this.$interviewId, '$interviewId'],
        [this.$interview, '$interview'],
        [this.$initialMessages, '$initialMessages']
      ]

      stores.forEach(([store, tag]) => {
        store.watch((val) => {
          if (!window.isDevMode) return

          try {
            console.log(
              `CallFixturesService: ${tag} set: ${JSON.stringify(val)}`
            )
          } catch (e) {
            console.log(`CallFixturesService: ${tag} set: ${val}`)
          }
        })
      })
    }

    return (): void => {
      unsubscribeEvent()
    }
  }
}

export const interviewFixturesService = new CallFixturesService()
